import {
  isReferrerAllowed,
  normalizePathname,
} from "@medceptor/meta-pixel/url";

import {
  FINISH_FORWARD_PARAM,
  MAX_NEXT_DEPTH,
  ONBOARDING_FINISH_PATH,
  ONBOARDING_PATH,
} from "@/lib/onboarding/routes";

/** Pages the Meta Pixel may report, as URL and referrer. Apart from
 *  meta-pixel.ts so the PageView chunk carries only this. */

/** The ONLY pages that may load the pixel or send it anything. An allowlist,
 *  never a denylist: Meta can classify an advertiser as health and wellness
 *  from the URLs its pixel sees, and any page added later must stay invisible
 *  to it until someone decides otherwise here (CLAUDE.md #45 b).
 *
 *  `/login` is deliberately absent: the proxy sends every signed-out deep link
 *  there as `/login?next=<the authed URL>`, so its URL routinely names a
 *  scenario. */
export const PIXEL_PAGES: ReadonlySet<string> = new Set([
  ONBOARDING_PATH,
  ONBOARDING_FINISH_PATH,
  "/signup",
]);

/** Paths a `?next=` or a same-origin referrer on a pixel page may name:
 *  fbevents.js sends both the full page URL and `document.referrer`. */
const NEUTRAL_PATHS: ReadonlySet<string> = new Set([
  ...PIXEL_PAGES,
  "/",
  "/login",
  "/pricing",
]);

/** Query keys a pixel page may carry. Anything else declines, so a parameter
 *  added later fails safe. `next` is the only one holding a URL, and it is
 *  checked recursively. */
const PARAM_KEYS: ReadonlySet<string> = new Set([
  "next",
  "step",
  FINISH_FORWARD_PARAM,
  "confirm",
  "oauth_error",
  "oauth_error_code",
  // Checkout's cancel URL back to onboarding (lib/billing/checkout-return.ts).
  "cancelled",
  // Ad attribution; `utm_*` is matched by prefix.
  "fbclid",
]);

function isNeutralUrl(
  url: URL,
  paths: ReadonlySet<string>,
  depth: number,
): boolean {
  // A fragment can carry auth tokens, and fbevents.js sends the whole href.
  if (url.hash) return false;
  if (!paths.has(normalizePathname(url.pathname))) return false;
  for (const [key, value] of url.searchParams) {
    if (!PARAM_KEYS.has(key) && !key.startsWith("utm_")) return false;
    if (key !== "next" || !value) continue;
    if (depth >= MAX_NEXT_DEPTH) return false;
    let target: URL;
    try {
      target = new URL(value, url);
    } catch {
      return false;
    }
    if (target.origin !== url.origin) return false;
    if (!isNeutralUrl(target, NEUTRAL_PATHS, depth + 1)) return false;
  }
  return true;
}

/** True when `href` (absolute) is an allowlisted path whose query names
 *  nothing else. */
export function isMetaPixelPageUrl(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  return isNeutralUrl(url, PIXEL_PAGES, 0);
}

/** A same-origin referrer must itself be a neutral URL, since it can name the
 *  authed page it came from. */
export function isNeutralReferrer(referrer: string, pageHref: string): boolean {
  return isReferrerAllowed(referrer, pageHref, (ref) =>
    isNeutralUrl(ref, NEUTRAL_PATHS, 0),
  );
}
